import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Drop, DropHalf, Waves, ArrowLineUp, Plus, Check, X } from "@phosphor-icons/react";
import { listProducts, createProduct } from "../lib/api";
import { useSiteOptions, mergeOptions } from "../lib/options";

export const WATER_PRESETS = [
  ["light", "Light", "A small top-up", DropHalf],
  ["normal", "Normal", "Until it drains", Drop],
  ["deep", "Deep soak", "Thorough soak", Waves],
  ["bottom", "Bottom water", "Tray from below", ArrowLineUp],
];
const STRENGTHS = [["quarter", "¼"], ["half", "½"], ["full", "Full"]];
const LAST_KEY = "gh-feed-last";

export function useLongPress(onLong, ms = 450) {
  const t = useRef(null);
  const fired = useRef(false);
  const clear = () => clearTimeout(t.current);
  return {
    fired,
    handlers: {
      onPointerDown: () => { fired.current = false; clear(); t.current = setTimeout(() => { fired.current = true; navigator.vibrate?.(8); onLong(); }, ms); },
      onPointerUp: clear,
      onPointerLeave: clear,
      onPointerCancel: clear,
      onContextMenu: (e) => { e.preventDefault(); clear(); fired.current = true; onLong(); },
    },
  };
}

function WaterPanel({ onPick }) {
  const opts = useSiteOptions();
  const presets = mergeOptions(WATER_PRESETS.map(([key, label, hint, Icon]) => ({ key, label, hint, Icon })), opts.water_presets);
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="water-presets">
      {presets.map(({ key: k, label, hint, amount, Icon = Drop }, i) => (
        <motion.button key={k} type="button" onClick={() => onPick(label, { amount: k })} data-testid={`water-preset-${k}`}
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03, type: "spring", stiffness: 420, damping: 30 }}
          className="rounded-2xl p-3 text-left bg-[#0A84FF]/[0.07] hover:bg-[#0A84FF]/[0.13] active:scale-[0.97] transition-[background-color,transform]">
          <Icon weight="fill" className="h-5 w-5 text-[#0A84FF]" />
          <span className="mt-2 block text-sm font-semibold leading-tight">{label}</span>
          <span className="block text-[11px] text-muted-foreground">{hint || amount || "Custom"}</span>
        </motion.button>
      ))}
    </div>
  );
}

function FeedPanel({ onPick }) {
  const last = (() => { try { return JSON.parse(localStorage.getItem(LAST_KEY)) || {}; } catch { return {}; } })();
  const [products, setProducts] = useState(null);
  const [product, setProduct] = useState(last.product || null);
  const [strength, setStrength] = useState(last.strength || "half");
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  useEffect(() => { listProducts().then((ps) => { setProducts(ps); if (!product && ps[0]) setProduct(ps[0].name); }).catch(() => setProducts([])); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    const p = await createProduct(name.trim());
    setProducts((ps) => [...(ps || []).filter((x) => x.id !== p.id), p].sort((a, b) => a.name.localeCompare(b.name)));
    setProduct(p.name); setName(""); setAdding(false);
  };
  const go = () => {
    localStorage.setItem(LAST_KEY, JSON.stringify({ product, strength }));
    const s = STRENGTHS.find(([k]) => k === strength)[1];
    onPick([product, `${s}${strength === "full" ? "" : " strength"}`].filter(Boolean).join(" · "), { product, strength });
  };
  const chip = (on) => `h-8 px-3 rounded-full text-xs font-medium transition-colors active:scale-[0.96] ${on ? "bg-foreground text-background" : "bg-muted/70 hover:bg-muted"}`;
  return (
    <div data-testid="feed-presets">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Product</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {(products || []).map((p) => <button key={p.id} type="button" onClick={() => setProduct(product === p.name ? null : p.name)} className={chip(product === p.name)} data-testid={`feed-product-${p.id}`}>{p.name}</button>)}
        {adding ? (
          <form onSubmit={add} className="flex items-center gap-1">
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Fish emulsion" maxLength={60}
              className="h-8 w-40 px-3 rounded-full bg-muted/70 text-xs outline-none focus:ring-2 focus:ring-primary/40" data-testid="feed-product-input" />
            <button type="submit" className="h-8 w-8 rounded-full bg-foreground text-background inline-flex items-center justify-center" aria-label="Save product" data-testid="feed-product-save"><Check weight="bold" className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => setAdding(false)} className="h-8 w-8 rounded-full hover:bg-muted inline-flex items-center justify-center" aria-label="Cancel"><X className="h-3.5 w-3.5" /></button>
          </form>
        ) : (
          <button type="button" onClick={() => setAdding(true)} className="h-8 pl-2.5 pr-3 rounded-full text-xs font-medium text-primary bg-primary/[0.08] hover:bg-primary/[0.14] inline-flex items-center gap-1" data-testid="feed-product-add"><Plus weight="bold" className="h-3 w-3" />Add product</button>
        )}
      </div>
      {products && !products.length && !adding && <p className="mt-2 text-[11px] text-muted-foreground">Save the fertilizers you use and pick them in one tap next time.</p>}
      <p className="mt-4 text-[11px] uppercase tracking-wider text-muted-foreground">Strength</p>
      <div className="mt-2 inline-grid grid-cols-3 p-1 rounded-full bg-muted/70">
        {STRENGTHS.map(([k, l]) => <button key={k} type="button" onClick={() => setStrength(k)} className={`h-8 px-4 rounded-full text-xs font-semibold transition-colors ${strength === k ? "bg-background shadow-sm" : "text-muted-foreground"}`} data-testid={`feed-strength-${k}`}>{l}</button>)}
      </div>
      <button type="button" onClick={go} data-testid="feed-log-btn"
        className="mt-4 w-full h-11 rounded-full bg-[#30D158] text-white text-sm font-semibold active:scale-[0.98] transition-transform">
        Log feeding
      </button>
    </div>
  );
}

export function PresetPanel({ type, onPick }) {
  return (
    <div className="w-[min(20rem,calc(100vw-2rem))]">
      <p className="text-sm font-semibold tracking-tight mb-3">{type === "watering" ? "How much water?" : "What did you feed?"}</p>
      {type === "watering" ? <WaterPanel onPick={onPick} /> : <FeedPanel onPick={onPick} />}
    </div>
  );
}
